import { ApiCard } from "../../../src/types/apigame";
import { CSSProperties, useCallback, useMemo } from "react";
import { API_MAP_CARD_ASSET_HEIGHT, API_MAP_CARD_ASSET_WIDTH, ApiMapCard } from "@/src/types/apimap";
import { useGame } from "@/src/contexts/game";
import GameMap from "@/src/models/map";

type GameCardProps = {
    card: ApiCard;
    mapCard?: never;
    onToggle?: (cardId: number) => void;
}

type MapCardProps = {
    card?: never;
    mapCard: ApiMapCard;
    onToggle?: never;
}

type CardProps = GameCardProps | MapCardProps;

type Props = CardProps & {
    className?: string;
    width?: number;
    height?: number;
    style?: CSSProperties;
    title?: string;
}

export default function GamePlaySingleCardView({
    card,
    className,
    mapCard,
    onToggle,
    width = API_MAP_CARD_ASSET_WIDTH,
    height = API_MAP_CARD_ASSET_HEIGHT,
    style,
    title,
}: Props) {
    const { game } = useGame();
    const gameCardInstanceId = card?.id;
    const referencedMapCardId = card?.cardId;

    const m = useMemo(
        () => new GameMap(game.map),
        [game],
    );

    const mapCardtoUse = useMemo(
        () => mapCard ?? m.getCardById(referencedMapCardId),
        [m, mapCard, referencedMapCardId],
    );

    const onClick = useCallback(
        () => {
            if (!onToggle || gameCardInstanceId == null) {
                return;
            }

            onToggle(gameCardInstanceId);
        },
        [onToggle, gameCardInstanceId],
    );

    // use a picture/img instead of NextJS's Image component
    //
    // In theory NextJS's Image component should be more optimized,
    // but it has some unneeded behavior:
    //
    // * It will try to optimize the image size based on width/height,
    //   which means proxying image reads through the nextjs server.
    //   This can be slow. These images are guaranteed to be small so
    //   this feels unnecessary
    //
    // * The default TTL of the image in cache is 60s, so the image gets stale
    //   and the browser often needs to re-fetch. This can happen when sitting
    //   in a game and earning a card. When this occurs, you see the
    //   "you got a card" dialog with a long delay for the image loading
    return <picture>
        <img
            key={card?.id ?? mapCardtoUse.id}
            className={className}
            src={m.getCardAssetUrl(mapCardtoUse)}
            alt={mapCardtoUse.name}
            width={width}
            height={height}
            onClick={onClick}
            style={style}
            title={title ?? mapCardtoUse.name}
        />
    </picture>;
}
